import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Popover } from "@mantine/core";
import { DatePicker, DatesProvider, MonthPicker } from "@mantine/dates";
import { addDays, addMonths, format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useState } from "react";

import Headline from "~/components/Headline/Headline";
import { type CalendarView, ViewSwitch } from "~/modules/dashboard/components/TimeTrackingCalendar/ViewSwitch.tsx";

type Props = {
	month: string;
	onMonthChange: (month: string) => void;
	onToday: () => void;
	// The view switch and the day mode exist when `onViewChange` is set.
	view?: CalendarView;
	onViewChange?: (view: CalendarView) => void;
	selectedDate: Date;
	onDayChange: (date: Date) => void;
};

const firstMonth = "2018-01";
// The app loads no Mantine CSS, so the picker needs its classes, as in DeliveryPeriodPickerPopover.
const monthPickerClassNames = {
	monthsListControl: `w-full rounded-md px-2 py-2 text-center text-sm hover:bg-primary-400 hover:text-white
		data-[disabled="true"]:text-gray-300 data-[disabled="true"]:hover:bg-white data-[selected]:bg-accent-500 data-[selected]:text-white`,
	monthsList: "w-full",
	calendarHeader: "flex h-10 justify-between",
	calendarHeaderLevel: "flex items-center justify-center gap-1 rounded-full px-4 hover:bg-gray-100",
	calendarHeaderControl: "flex size-10 items-center justify-center rounded-full hover:bg-gray-100 data-[disabled]:opacity-30",
	calendarHeaderControlIcon: "text-gray-400 data-[direction='previous']:rotate-90 data-[direction='next']:-rotate-90",
	yearsListControl: `w-full rounded-md px-2 py-2 text-center text-sm hover:bg-primary-400 hover:text-white
		data-[disabled="true"]:text-gray-300 data-[disabled="true"]:hover:bg-white`,
	yearsList: "w-full",
};

const dayPickerClassNames = {
	...monthPickerClassNames,
	month: "border-collapse",
	weekday: "text-sm font-light capitalize text-gray-400",
	day: `inline-flex size-8 items-center justify-center rounded-md text-sm hover:bg-primary-400 hover:text-white
		data-[outside]:text-gray-500 data-[disabled]:text-gray-300 data-[disabled]:hover:bg-white
		data-[selected]:bg-primary-500 data-[selected]:text-white data-[weekend]:text-accent-500 data-[weekend]:hover:text-white`,
};

const pillButtonClass = "flex h-7 items-center px-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent";

/**
 * The header bar of the dashboard calendar. The dashboard has its own, because the time tracking page keeps the
 * shared `MonthAndYearNavigation` with its two selects, and the picker of this header would change that page.
 * The months run from January 2018 to December of the current year, as the years of that navigation.
 */
export function CalendarHeader({ month, onMonthChange, onToday, view = "month", onViewChange, selectedDate, onDayChange }: Props) {
	const [pickerIsOpen, setPickerIsOpen] = useState(false);
	const firstOfMonth = parseISO(`${month}-01`);
	const lastMonth = `${new Date().getFullYear()}-12`;
	const isDayView = view === "day" && !!onViewChange;
	const firstDayKey = `${firstMonth}-01`;
	const lastDayKey = `${lastMonth}-31`;
	const firstDay = parseISO(firstDayKey);
	const lastDay = parseISO(lastDayKey);
	const selectedKey = format(selectedDate, "yyyy-MM-dd");
	const step = (amount: number) => onMonthChange(format(addMonths(firstOfMonth, amount), "yyyy-MM"));
	const stepDay = (amount: number) => onDayChange(addDays(selectedDate, amount));

	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 p-3">
			<Headline type="h3" size="lg" weight="semibold" className="mr-auto uppercase">{format(firstOfMonth, "MMMM yyyy", { locale: de })}</Headline>
			<div className="flex h-7 items-center divide-x divide-gray-300 overflow-hidden rounded-full border border-gray-300">
				<button type="button"
						aria-label={isDayView ? "Vorheriger Tag" : "Vorheriger Monat"}
						disabled={isDayView ? selectedKey <= firstDayKey : month <= firstMonth}
						onClick={() => isDayView ? stepDay(-1) : step(-1)}
						className={pillButtonClass}>
					<ChevronLeftIcon className="size-4" />
				</button>
				<Popover opened={pickerIsOpen}
						 onChange={setPickerIsOpen}
						 position="bottom"
						 shadow="md">
					<Popover.Target>
						<button type="button"
								onClick={() => setPickerIsOpen((isOpen) => !isOpen)}
								className={pillButtonClass}>
							{isDayView ? "Tag" : "Monat"}
						</button>
					</Popover.Target>
					<Popover.Dropdown className="!rounded-lg !border !border-gray-200 !bg-white !p-2">
						<div className="w-72">
							<DatesProvider settings={{ locale: "de" }}>
								{isDayView ? (
									<DatePicker classNames={dayPickerClassNames}
												defaultDate={selectedDate}
												value={selectedDate}
												minDate={firstDay}
												maxDate={lastDay}
												onChange={(date) => {
													if (!date) return;
													onDayChange(date);
													setPickerIsOpen(false);
												}} />
								) : (
									<MonthPicker allowDeselect={false}
												 classNames={monthPickerClassNames}
												 value={firstOfMonth}
												 minDate={firstDay}
												 maxDate={lastDay}
												 onChange={(date) => {
													 if (!date) return;
													 onMonthChange(format(date, "yyyy-MM"));
													 setPickerIsOpen(false);
												 }} />
								)}
							</DatesProvider>
						</div>
					</Popover.Dropdown>
				</Popover>
				<button type="button"
						aria-label={isDayView ? "Nächster Tag" : "Nächster Monat"}
						disabled={isDayView ? selectedKey >= lastDayKey : month >= lastMonth}
						onClick={() => isDayView ? stepDay(1) : step(1)}
						className={pillButtonClass}>
					<ChevronRightIcon className="size-4" />
				</button>
			</div>
			{onViewChange && <ViewSwitch view={view}
										 onChange={onViewChange} />}
			<button type="button"
					onClick={onToday}
					className="ml-auto h-7 rounded-full bg-primary-500 px-3 text-xs font-medium text-white hover:bg-primary-700 sm:ml-0">
				Heute
			</button>
		</div>
	);
}
